import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import StarRating from './StarRating';
import './ReviewWidget.css';

/**
 * ReviewWidget — การ์ดให้ดาว+รีวิวบนหน้าหลัก (Dashboard)
 * แตะดาวเพื่อให้คะแนน → กรอกความเห็น (ไม่บังคับ) → ส่ง
 * ต้องยืนยันอีเมลก่อนจึงรีวิวได้ (server บังคับด้วย requireVerifiedEmail)
 */
function ReviewWidget() {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');

    const emailVerified = !!user?.email_verified;

    const load = useCallback(async () => {
        try {
            const res = await api.get('/reviews/summary');
            setSummary(res.data);
            if (res.data.myReview) {
                setRating(res.data.myReview.rating);
                setComment(res.data.myReview.comment || '');
                setDisplayName(res.data.myReview.display_name || '');
            }
        } catch (e) {
            // เงียบไว้ — widget ไม่ critical
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleStar = (n) => {
        if (!emailVerified) return;
        setRating(n);
        setExpanded(true);
        setMsg('');
    };

    const submit = async () => {
        if (!rating) return;
        setSaving(true);
        setMsg('');
        try {
            await api.post('/reviews', { rating, comment, display_name: displayName });
            setMsg('ขอบคุณสำหรับรีวิว 💙');
            setExpanded(false);
            await load();
        } catch (e) {
            if (e.response?.status === 403) setMsg('กรุณายืนยันอีเมลก่อนรีวิว');
            else setMsg(e.response?.data?.error || 'บันทึกไม่สำเร็จ');
        } finally {
            setSaving(false);
        }
    };

    if (!summary) return null;

    return (
        <div className="review-widget">
            <div className="rw-head">
                <div className="rw-title">
                    <span className="rw-title-main">ให้คะแนนระบบ iMoney</span>
                    {summary.count > 0 && (
                        <span className="rw-avg">
                            <StarRating value={Math.round(summary.average)} readOnly size={18} />
                            <b>{summary.average.toFixed(1)}</b>
                            <span className="rw-count">({summary.count} รีวิว)</span>
                        </span>
                    )}
                </div>
                <Link to="/reviews" className="rw-all-link">ดูรีวิวทั้งหมด →</Link>
            </div>

            {!emailVerified ? (
                <div className="rw-gate">
                    ⭐ ยืนยันอีเมลก่อน จึงจะรีวิวได้ — <Link to="/profile#email">ยืนยันอีเมล</Link>
                </div>
            ) : (
                <div className="rw-form">
                    <div className="rw-stars-row">
                        <span className="rw-prompt">
                            {summary.myReview ? 'รีวิวของคุณ (แตะเพื่อแก้ไข):' : 'แตะดาวเพื่อให้คะแนน:'}
                        </span>
                        <StarRating value={rating} onChange={handleStar} size={32} />
                    </div>

                    {expanded && (
                        <div className="rw-expand">
                            <input
                                className="rw-name-input"
                                type="text"
                                placeholder="ชื่อที่จะแสดงบนรีวิว (ไม่บังคับ)"
                                maxLength={100}
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                            />
                            <textarea
                                className="rw-textarea"
                                placeholder="เล่าประสบการณ์ใช้งาน (ไม่บังคับ)"
                                maxLength={1000}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            />
                            <div className="rw-actions">
                                <button className="rw-save" onClick={submit} disabled={saving || !rating}>
                                    {saving ? 'กำลังบันทึก...' : (summary.myReview ? 'อัปเดตรีวิว' : 'ส่งรีวิว')}
                                </button>
                                <button className="rw-cancel" onClick={() => setExpanded(false)} disabled={saving}>
                                    ยกเลิก
                                </button>
                            </div>
                        </div>
                    )}

                    {msg && <div className="rw-msg">{msg}</div>}
                </div>
            )}
        </div>
    );
}

export default ReviewWidget;
